import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight,
  Heart,
  Sparkles,
  Shield,
  Award,
  Users,
  Baby,
  Laptop,
  TrendingUp,
  Compass,
  Flame,
  GraduationCap,
  CheckCircle2,
  Dumbbell,
  Quote,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ABOUT } from "@/lib/content";
import SEO, { personJsonLd, faqJsonLd } from "@/components/SEO";

const ICONS = {
  Heart, Sparkles, Shield, Award, Users, Baby, Laptop, TrendingUp,
  Compass, Flame, GraduationCap, CheckCircle2, Dumbbell,
};

function Icon({ name, size = 22, className = "text-[#E8A6B8]" }) {
  const C = ICONS[name] || Heart;
  return <C size={size} strokeWidth={1.5} className={className} />;
}

const BELIEFS = [
  { title: "Women deserve to feel strong", icon: "Flame" },
  { title: "Fitness should fit around real life", icon: "Compass" },
  { title: "Confidence is built through consistency", icon: "TrendingUp" },
  { title: "Strength training is empowering", icon: "Dumbbell" },
  { title: "Progress doesn't require perfection", icon: "Sparkles" },
];

const PHILOSOPHY = [
  "Realistic, sustainable coaching",
  "Beginner-friendly support",
  "Confidence-focused training",
  "Women-only environment",
  "Strength over extremes",
  "Building long-term habits",
];

const QUALIFICATIONS = [
  {
    title: "Women's Strength Coach",
    body: "Years of hands-on experience helping women feel strong, confident and at home in their bodies — beginners welcome, always.",
    icon: "Heart",
  },
  {
    title: "NASM Women's Health Studies",
    body: "Ongoing learning in women's health, hormonal cycles, postpartum recovery and what makes training genuinely work for women's bodies.",
    icon: "GraduationCap",
  },
  {
    title: "Personal Training Qualified",
    body: "Fully qualified and insured — but more importantly, focused on coaching you like a real person, not a number on a clipboard.",
    icon: "Award",
  },
  {
    title: "Women-only Coaching Specialist",
    body: "Every plan, program and check-in is built for women — beginners, returning lifters, postpartum mums, and everyone in between.",
    icon: "Users",
  },
  {
    title: "Strength & Confidence Focused",
    body: "Training that's about how you feel — capable, calm, in control — not about how you look in a mirror.",
    icon: "Shield",
  },
];

const WHO_I_HELP = [
  {
    title: "Beginners",
    body: "Learn the fundamentals of strength training in a supportive, judgement-free space.",
    icon: "Sparkles",
    to: "/services#beginner-gym",
  },
  {
    title: "Mums rebuilding strength",
    body: "Postpartum strength coaching that meets you exactly where your body is today.",
    icon: "Baby",
    to: "/services#postpartum",
  },
  {
    title: "Women wanting gym confidence",
    body: "Walk into any gym knowing exactly what to do — and that you belong there.",
    icon: "TrendingUp",
    to: "/services#beginner-gym",
  },
  {
    title: "Women wanting online support",
    body: "Online coaching for women, anywhere — with weekly check-ins and accountability.",
    icon: "Laptop",
    to: "/services#online-strength",
  },
];

const FAQ_ITEMS = [
  {
    q: "Do you work with beginners?",
    a: "Yes — most of my clients are. If you've never lifted a weight in your life, you're exactly who this is for. We start gently, build slowly, and you'll feel supported every step.",
  },
  {
    q: "Is your coaching women-only?",
    a: "Always. HerFitHub is a women-only space by design — supportive, judgement-free, and built to help you feel empowered, not intimidated.",
  },
  {
    q: "Do you offer online coaching?",
    a: "Yes — online coaching for women is available wherever you are. You'll get a personalised plan, weekly check-ins, technique reviews and someone in your corner.",
  },
  {
    q: "Can mums join?",
    a: "Absolutely. Postpartum strength coaching is one of my favourite things to do — rebuilding gently, safely, and around real-life motherhood. There's no wrong stage to start.",
  },
  {
    q: "Do I need gym experience?",
    a: "Not even a little. Beginner gym coaching for women is built for exactly this — we'll teach you the basics with kindness and zero pressure.",
  },
];

export default function About() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) {
      window.scrollTo({ top: 0, behavior: "instant" });
      return;
    }
    const id = hash.replace("#", "");
    const target = document.getElementById(id);
    if (target) {
      setTimeout(() => {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    }
  }, [hash]);

  return (
    <div data-testid="about-page" className="bg-[#F6EFE9]">
      <SEO
        title="About Maya — Women's Strength Coach London"
        description="Meet Maya — London-based women's strength coach helping women build strength, confidence and trust in their bodies through realistic coaching that fits around real life."
        path="/about"
        keywords="women's strength coach London, women's personal trainer London, postpartum strength coaching, beginner gym coaching for women"
        jsonLd={[personJsonLd, faqJsonLd(FAQ_ITEMS)]}
      />
      <Hero />
      <Story />
      <Beliefs />
      <Philosophy />
      <Qualifications />
      <WhoIHelp />
      <FAQ />
      <FinalCTA />
    </div>
  );
}

/* ---------- HERO ---------- */
function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 fade-in">
            <p className="eyebrow mb-6">About Maya · HerFitHub</p>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#3A3A3A]">
              Helping Women Build Strength,{" "}
              <span className="italic text-[#E8A6B8]">
                Confidence & Trust In Their Bodies Again
              </span>
            </h1>
            <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
              Women's strength coaching designed to help women feel stronger
              physically and mentally through realistic training that fits
              around real life.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/booking" className="btn-pink" data-testid="hero-start-journey">
                Start Your Journey <ArrowRight size={16} />
              </Link>
              <Link to="/services" className="btn-outline" data-testid="hero-explore-services">
                Explore Services
              </Link>
            </div>
            <p
              className="mt-7 text-[11px] sm:text-xs uppercase tracking-[0.16em] text-[#5C5C5C] flex flex-wrap items-center gap-x-3 gap-y-1.5"
              data-testid="hero-trust-line"
            >
              <span>Women-only coaching</span>
              <span className="text-[#E8A6B8]">•</span>
              <span>Beginner friendly</span>
              <span className="text-[#E8A6B8]">•</span>
              <span>Online & London-based</span>
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F3C9D3]">
              <img
                src={ABOUT.image}
                alt="Maya — women's strength coach London"
                className="absolute inset-0 w-full h-full object-cover"
                data-testid="about-hero-image"
              />
            </div>
          </div>
        </div>
      </div>
      <div aria-hidden className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#F3C9D3]/40 blur-3xl" />
    </section>
  );
}

/* ---------- MY STORY ---------- */
function Story() {
  return (
    <section className="bg-white py-20 lg:py-28 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* IMAGE */}
          <div className="lg:col-span-5 relative">
            {/* Decorative blush shape behind the image */}
            <div
              aria-hidden
              className="absolute -top-6 -left-6 w-3/4 h-3/4 rounded-[2.5rem] bg-[#F3C9D3]/40 -z-0"
            />
            <div
              aria-hidden
              className="absolute -bottom-8 -right-6 w-1/2 h-1/2 rounded-full bg-[#E8A6B8]/15 blur-3xl -z-0"
            />
            <div className="relative z-10 rounded-[2rem] overflow-hidden bg-[#F3C9D3]/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(232,166,184,0.28)] shadow-[0_18px_44px_rgba(232,166,184,0.18)]">
              <img
                src="https://customer-assets.emergentagent.com/job_empower-with-maya/artifacts/sbfau8pz_WhatsApp%20Image%202026-05-22%20at%2001.17.58.jpeg"
                alt="Maya — women's strength coach London"
                loading="lazy"
                decoding="async"
                className="w-full h-auto aspect-[4/5] object-cover"
                data-testid="story-image"
              />
              {/* Elegant overlay quote */}
              <div className="absolute bottom-5 left-5 right-5 sm:left-6 sm:right-6 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4 shadow-sm">
                <p className="font-serif-display italic text-sm sm:text-base text-[#3A3A3A] leading-snug">
                  "Helping women build strength, confidence & trust in themselves again."
                </p>
              </div>
            </div>
            {/* Small signature accent */}
            <p className="mt-6 text-center text-[11px] uppercase tracking-[0.18em] text-[#E8A6B8] font-semibold lg:hidden">
              Maya · HerFitHub
            </p>
          </div>

          {/* STORY TEXT */}
          <div className="lg:col-span-7">
            <p className="eyebrow mb-3" data-testid="story-eyebrow">My Story</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
              Hi, I'm <span className="italic text-[#E8A6B8]">Maya</span> 🤍
            </h2>
            <p className="heart-divider text-left mt-3">♡</p>

            <div className="mt-8 space-y-6 text-base sm:text-lg leading-relaxed text-[#5C5C5C]">
              <p>
                I didn't grow up thinking I'd be a strength coach. For a long time
                I was the girl who'd walk into a gym, eye up the weights area, then
                quietly tuck myself onto a treadmill. I wanted to be strong — I
                just had no idea where to begin.
              </p>
              <p>
                What changed everything wasn't a transformation photo. It was the
                first time I picked up a barbell, did it properly, and walked out
                feeling <em>capable</em>. Steady. Calmer in my own skin. That
                feeling is what I've been chasing — and helping other women find —
                ever since.
              </p>
              <p>
                These days I coach women through the same shift. Beginners taking
                their first nervous steps. Mums rebuilding strength after children,
                doing it on three hours of sleep and a half-cold coffee. Women
                coming back to themselves after a long time away.
              </p>
              <p>
                I get the real-life stuff because I live it too. Tired mornings,
                busy weeks, bodies that don't always cooperate. Coaching, for me,
                has to fit around all of that — or it doesn't work. So we keep it
                realistic. Honest. Soft when you need it, structured when you're
                ready.
              </p>
              <p>
                HerFitHub is a <strong>women-only</strong> space because I
                remember what it felt like to be intimidated. I never wanted to
                build something that did that to anyone else. So this is yours.
                Warm. Supportive. Yours to grow into.
              </p>
              <p className="font-serif-display italic text-2xl text-[#3A3A3A] pt-2">
                You don't have to start strong. You just have to start.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- WHAT I BELIEVE ---------- */
function Beliefs() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">What I Believe</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            The principles behind <span className="italic text-[#E8A6B8]">every session</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {BELIEFS.map((b, i) => (
            <article
              key={b.title}
              className="bg-white rounded-[1.5rem] p-6 border border-[#F3C9D3]/40 text-center transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)]"
              data-testid={`belief-${i}`}
            >
              <div className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 mb-4 transition-transform duration-300 hover:scale-110">
                <Icon name={b.icon} size={22} />
              </div>
              <p className="font-serif-display text-lg text-[#3A3A3A] leading-snug">
                {b.title}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- PHILOSOPHY ---------- */
function Philosophy() {
  return (
    <section className="bg-[#F3C9D3]/30 py-20 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1">
          <p className="eyebrow mb-4">My Coaching Philosophy</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight">
            Strength built <br />
            <span className="italic text-[#E8A6B8]">the right way</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
            My philosophy is simple: realistic, sustainable coaching that works
            with your life — not against it. I believe in beginner-friendly
            support, confidence over extremes, and the kind of training that
            builds long-term habits, not quick fixes.
          </p>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#5C5C5C] max-w-xl">
            As a women's personal trainer in London, my coaching prioritises
            how you feel in your body — capable, calm, in control — over how
            you look in a mirror.
          </p>
          <ul className="mt-10 grid sm:grid-cols-2 gap-3 max-w-xl">
            {PHILOSOPHY.map((p) => (
              <li
                key={p}
                className="flex items-center gap-3 bg-white/80 backdrop-blur-sm rounded-2xl px-4 py-3 border border-white/50"
              >
                <CheckCircle2 size={18} className="text-[#E8A6B8] flex-shrink-0" strokeWidth={1.6} />
                <span className="text-sm font-medium text-[#3A3A3A]">{p}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-6 order-1 lg:order-2">
          <div className="relative rounded-[2rem] overflow-hidden aspect-[4/5] bg-[#F3C9D3]">
            <img
              src="https://images.pexels.com/photos/36387535/pexels-photo-36387535.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
              alt="Strength training for women — postpartum strength coaching session"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4">
              <Quote size={18} className="text-[#E8A6B8] mb-2" strokeWidth={1.5} />
              <p className="font-serif-display italic text-lg text-[#3A3A3A] leading-snug">
                "Strong, capable and consistent — never intimidated."
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* SOFT MID-PAGE CTA */}
      <div className="max-w-4xl mx-auto px-6 lg:px-10 mt-20 lg:mt-24">
        <div
          className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 p-10 sm:p-12 text-center"
          data-testid="mid-cta-card"
        >
          <p className="eyebrow mb-3 text-[#E8A6B8]">A Gentle Next Step</p>
          <h3 className="font-serif-display text-3xl sm:text-4xl text-[#3A3A3A] leading-tight">
            Ready to start <span className="italic text-[#E8A6B8]">rebuilding your strength?</span>
          </h3>
          <p className="mt-4 text-[#5C5C5C] max-w-md mx-auto text-sm sm:text-base">
            Find the coaching option that feels right for you — at your pace, in your time.
          </p>
          <Link
            to="/services"
            className="btn-pink mt-7 inline-flex"
            data-testid="mid-cta-explore"
          >
            Explore Coaching <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ---------- QUALIFICATIONS ---------- */
function Qualifications() {
  return (
    <section id="qualifications" className="py-20 lg:py-28 scroll-mt-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">Qualifications & Experience</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Trained, qualified, <span className="italic text-[#E8A6B8]">trusted</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {QUALIFICATIONS.map((q, i) => (
            <article
              key={q.title}
              className="bg-white rounded-[1.75rem] p-7 border border-[#F3C9D3]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(232,166,184,0.2)]"
              data-testid={`qualification-${i}`}
            >
              <div className="inline-flex p-3 rounded-2xl bg-[#F3C9D3]/40 mb-5">
                <Icon name={q.icon} size={22} />
              </div>
              <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                {q.title}
              </h3>
              <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">{q.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- WHO I HELP ---------- */
function WhoIHelp() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="eyebrow mb-3">Who I Help</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Coaching that <span className="italic text-[#E8A6B8]">meets you where you are</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {WHO_I_HELP.map((c, i) => (
            <Link
              key={c.title}
              to={c.to}
              className="group bg-[#F6EFE9] rounded-[1.75rem] p-7 border border-[#F3C9D3]/30 transition-all duration-300 hover:-translate-y-1 hover:bg-white hover:shadow-[0_18px_40px_rgba(232,166,184,0.18)] flex flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8A6B8] focus-visible:ring-offset-2"
              data-testid={`about-who-card-${i}`}
              aria-label={`${c.title} — learn more`}
            >
              <div className="inline-flex p-3 rounded-2xl bg-white mb-5 border border-[#F3C9D3]/40 self-start transition-transform duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]">
                <Icon name={c.icon} size={22} />
              </div>
              <h3 className="font-serif-display text-xl text-[#3A3A3A] leading-snug">
                {c.title}
              </h3>
              <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed flex-1">{c.body}</p>
              <span className="mt-6 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold text-[#3A3A3A] group-hover:text-[#E8A6B8] transition-colors">
                Learn More
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- FAQ ---------- */
function FAQ() {
  return (
    <section className="py-20 lg:py-28">
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <p className="eyebrow mb-3">FAQ</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl text-[#3A3A3A]">
            Your questions, <span className="italic text-[#E8A6B8]">answered</span>
          </h2>
        </div>
        <Accordion
          type="single"
          collapsible
          className="bg-white rounded-[2rem] border border-[#F3C9D3]/40 px-6 sm:px-8 divide-y divide-[#F3C9D3]/30"
        >
          {FAQ_ITEMS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-0"
              data-testid={`about-faq-${i}`}
            >
              <AccordionTrigger className="text-left text-[#3A3A3A] font-medium text-base sm:text-lg py-6 hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C5C5C] text-sm sm:text-base leading-relaxed pb-6">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- FINAL CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-6 lg:px-10 pb-24 pt-4">
      <div
        className="max-w-6xl mx-auto rounded-[2rem] overflow-hidden relative text-center"
        style={{
          background:
            "linear-gradient(135deg, #E8A6B8 0%, #F3C9D3 50%, #F6EFE9 100%)",
        }}
      >
        <div className="relative px-6 sm:px-10 py-20 sm:py-24">
          <p className="eyebrow text-[#3A3A3A]/80 mb-4">Ready When You Are</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3A3A3A] leading-tight max-w-2xl mx-auto">
            You don't have to do this <span className="italic">alone</span>
          </h2>
          <p className="mt-7 text-base sm:text-lg text-[#3A3A3A]/80 leading-relaxed max-w-xl mx-auto">
            Start your strength journey with support, structure and a coach who's
            actually in your corner.
          </p>
          <Link
            to="/booking"
            className="mt-10 inline-flex items-center justify-center gap-2 bg-[#3A3A3A] hover:bg-black text-white rounded-full px-10 py-4 text-[12px] uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5 shadow-xl"
            data-testid="about-final-cta"
          >
            Book Consultation <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
